// Catat paket / Ubah paket (Pantau Paket): the paket as written in SiRUP or
// SPSE. Name required; pagu typed as digits and shown grouped; the link is
// stored only (http(s)), never opened by the app on its own.
import { useEffect, useId, useState, type FormEvent } from "react";
import type { WatchInput, WatchItem, WatchStatus } from "../api";
import { formatCount } from "../lib/format";
import { hpsDigits, isLink } from "../project/PaketEditDialog";
import { Dialog, Field } from "../ui/components";
import { JENIS_OPTIONS, METODE_OPTIONS, WATCH_STATUS_LABEL, WATCH_STATUS_ORDER } from "./shared";

export interface WatchDialogProps {
  open: boolean;
  /** null: a new record. */
  item: WatchItem | null;
  busy: boolean;
  error: string | null;
  onSubmit: (input: WatchInput) => void;
  onClose: () => void;
}

const squash = (v: string) => v.replace(/\s+/g, " ").trim();

export function WatchDialog({ open, item, busy, error, onSubmit, onClose }: WatchDialogProps) {
  const id = useId();
  const formId = `${id}-form`;
  const [nama, setNama] = useState("");
  const [instansi, setInstansi] = useState("");
  const [metode, setMetode] = useState("");
  const [jenis, setJenis] = useState("");
  const [kode, setKode] = useState("");
  const [pagu, setPagu] = useState("");
  const [tenggat, setTenggat] = useState("");
  const [link, setLink] = useState("");
  const [catatan, setCatatan] = useState("");
  const [status, setStatus] = useState<WatchStatus>("dipantau");
  const [linkError, setLinkError] = useState<string | null>(null);

  // Every opening starts from the stored values (or empty for a new record).
  useEffect(() => {
    if (!open) return;
    setNama(item?.nama ?? "");
    setInstansi(item?.instansi ?? "");
    setMetode(item?.metode ?? "");
    setJenis(item?.jenisPengadaan ?? "");
    setKode(item?.kode ?? "");
    setPagu(item?.pagu == null ? "" : String(Math.round(item.pagu)));
    setTenggat(item?.tenggat ?? "");
    setLink(item?.link ?? "");
    setCatatan(item?.catatan ?? "");
    setStatus(item?.status ?? "dipantau");
    setLinkError(null);
  }, [open, item]);

  const ready = squash(nama).length > 0 && !busy;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    const url = link.trim();
    if (url && !isLink(url)) {
      setLinkError("Tautan harus diawali http:// atau https://");
      return;
    }
    setLinkError(null);
    onSubmit({
      nama: squash(nama),
      instansi: squash(instansi) || null,
      metode: squash(metode) || null,
      jenisPengadaan: jenis || null,
      kode: squash(kode) || null,
      pagu: pagu ? Number(pagu) : null,
      tenggat: tenggat || null,
      link: url || null,
      catatan: catatan.trim() || null,
      status,
    });
  };

  return (
    <Dialog
      open={open}
      title={item ? "Ubah paket" : "Catat paket"}
      description="Salin data paket dari SiRUP atau SPSE. Periksa ulang jadwal dan nilai di portal resmi."
      busy={busy}
      onClose={onClose}
      footer={
        <button type="submit" form={formId} className="btn btn-primary" disabled={!ready} aria-busy={busy || undefined}>
          Simpan
        </button>
      }
    >
      <form id={formId} className="pj-form pk-form" onSubmit={submit} noValidate>
        <Field id={`${id}-nama`} label="Nama paket" required error={error}>
          <input id={`${id}-nama`} data-autofocus="" autoComplete="off" value={nama} maxLength={300} readOnly={busy} onChange={(e) => setNama(e.currentTarget.value)} />
        </Field>
        <Field id={`${id}-instansi`} label="Instansi">
          <input id={`${id}-instansi`} autoComplete="off" value={instansi} maxLength={200} readOnly={busy} onChange={(e) => setInstansi(e.currentTarget.value)} />
        </Field>
        <div className="pk-form-row">
          <Field id={`${id}-metode`} label="Metode pemilihan">
            <input id={`${id}-metode`} list={`${id}-metode-list`} autoComplete="off" value={metode} maxLength={100} readOnly={busy} onChange={(e) => setMetode(e.currentTarget.value)} />
            <datalist id={`${id}-metode-list`}>
              {METODE_OPTIONS.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </Field>
          <Field id={`${id}-jenis`} label="Jenis pengadaan">
            <select id={`${id}-jenis`} value={jenis} disabled={busy} onChange={(e) => setJenis(e.currentTarget.value)}>
              <option value="">Belum dipilih</option>
              {JENIS_OPTIONS.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="pk-form-row">
          <Field id={`${id}-kode`} label="Kode RUP atau kode paket">
            <input id={`${id}-kode`} autoComplete="off" value={kode} maxLength={100} readOnly={busy} onChange={(e) => setKode(e.currentTarget.value)} />
          </Field>
          <Field id={`${id}-pagu`} label="Pagu atau HPS (Rp)">
            <input id={`${id}-pagu`} inputMode="numeric" autoComplete="off" placeholder="mis. 450.000.000" value={pagu ? formatCount(Number(pagu)) : ""} readOnly={busy} onChange={(e) => setPagu(hpsDigits(e.currentTarget.value))} />
          </Field>
        </div>
        <div className="pk-form-row">
          <Field id={`${id}-tenggat`} label="Tenggat">
            <input id={`${id}-tenggat`} type="date" value={tenggat} readOnly={busy} onChange={(e) => setTenggat(e.currentTarget.value)} />
          </Field>
          <Field id={`${id}-status`} label="Status">
            <select id={`${id}-status`} value={status} disabled={busy} onChange={(e) => setStatus(e.currentTarget.value as WatchStatus)}>
              {WATCH_STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {WATCH_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field id={`${id}-link`} label="Tautan paket" error={linkError} hint="Disimpan sebagai catatan; dibuka hanya bila Anda memilihnya.">
          <input id={`${id}-link`} type="url" inputMode="url" autoComplete="off" placeholder="https://spse.inaproc.id/..." value={link} maxLength={500} readOnly={busy} aria-invalid={linkError ? true : undefined} onChange={(e) => setLink(e.currentTarget.value)} />
        </Field>
        <Field id={`${id}-catatan`} label="Catatan">
          <textarea id={`${id}-catatan`} rows={3} value={catatan} maxLength={4000} readOnly={busy} onChange={(e) => setCatatan(e.currentTarget.value)} />
        </Field>
      </form>
    </Dialog>
  );
}
